import type { ThemeMode } from '@streamlain/theme';
import { type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { completeBootTrace, recordBootEvent } from './boot-trace';

const BOOT_SPLASH_FADE_MS = 750;
const BOOT_SPLASH_UNDERLAY_TIMEOUT_MS = 1200;
const BOOT_SPLASH_READY_FALLBACK_MS = 80;
const BOOT_SPLASH_UNDERLAY_POLL_MS = 50;

export type BootSplashProgress = {
  label: string;
  detail?: string;
  progress?: number;
  mode?: 'progress' | 'indeterminate' | 'error';
};

export function updateBootSplashProgress({
  label,
  detail = '',
  progress,
  mode = progress === undefined ? 'indeterminate' : 'progress',
}: BootSplashProgress) {
  const splash = document.getElementById('sl-boot-splash');
  if (!splash) return;

  splash.setAttribute('data-demo-progress', 'true');
  splash.setAttribute('data-progress-mode', mode);
  splash.setAttribute('aria-hidden', 'false');
  const labelElement = splash.querySelector<HTMLElement>(
    '.sl-boot-progress-step'
  );
  const detailElement = splash.querySelector<HTMLElement>(
    '.sl-boot-progress-detail'
  );
  const fillElement = splash.querySelector<HTMLElement>(
    '.sl-boot-progress-fill'
  );
  if (labelElement) labelElement.textContent = label;
  if (detailElement) detailElement.textContent = detail;
  if (fillElement && progress !== undefined) {
    const safeProgress = Math.min(1, Math.max(0, progress));
    fillElement.style.width = `${Math.round(safeProgress * 100)}%`;
  }
}

/**
 * Keeps a still visible boot splash on the app's resolved theme mode. The boot
 * script picks the splash mode once at page load; when the app theme then
 * resolves differently, content rendered into the splash (e.g. the Demo name
 * entry) would otherwise use dark tokens on a light splash or vice versa.
 */
export function syncBootSplashTheme(mode: ThemeMode) {
  const root = document.documentElement;
  if (!root.hasAttribute('data-sl-boot-theme')) return;
  root.setAttribute('data-sl-boot-theme', mode);
}

/**
 * Hides the progress again so the splash action cross-fades back in, e.g.
 * after the Demo rejected a visitor name.
 */
export function clearBootSplashProgress() {
  const splash = document.getElementById('sl-boot-splash');
  if (!splash) return;
  splash.removeAttribute('data-demo-progress');
  splash.removeAttribute('data-progress-mode');
}

/**
 * Renders an action (e.g. the Demo start button) inside the boot splash, below
 * the brand lockup, so the same splash can first ask for the action and then
 * show progress without a reload. Once an app shell has dismissed the splash,
 * the action renders in place instead.
 */
export function BootSplashAction({ children }: { children: ReactNode }) {
  const [slot] = useState(() =>
    document.querySelector<HTMLElement>('#sl-boot-splash .sl-boot-action')
  );

  useEffect(() => {
    const splash = slot?.closest('#sl-boot-splash');
    if (!splash) return;
    const hidden = splash.getAttribute('aria-hidden');
    splash.setAttribute('aria-hidden', 'false');
    return () => {
      if (
        hidden !== null &&
        splash.getAttribute('data-demo-progress') !== 'true'
      )
        splash.setAttribute('aria-hidden', hidden);
    };
  }, [slot]);

  return slot ? createPortal(children, slot) : children;
}

function isDarkBootTheme() {
  return document.documentElement.getAttribute('data-sl-boot-theme') === 'dark';
}

function parseRgb(color: string): [number, number, number] | null {
  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) {
    return null;
  }

  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function isWhiteColor(color: string): boolean {
  const rgb = parseRgb(color);
  if (!rgb) {
    return false;
  }

  return rgb.every(channel => channel >= 245);
}

function readVisibleBackground(element: Element | null): string | null {
  let current: Element | null = element;
  while (current) {
    const background = getComputedStyle(current).backgroundColor;
    if (
      background &&
      background !== 'transparent' &&
      !background.endsWith(', 0)')
    ) {
      return background;
    }
    current = current.parentElement;
  }

  return getComputedStyle(document.body).backgroundColor;
}

function isBootUnderlayReady(): boolean {
  if (!isDarkBootTheme()) {
    return true;
  }

  const element = document.elementFromPoint(
    window.innerWidth / 2,
    window.innerHeight / 2
  );
  const background = readVisibleBackground(element);
  return background ? !isWhiteColor(background) : false;
}

function waitForBootUnderlayReady(callback: () => void) {
  const startedAt = performance.now();

  const tick = () => {
    if (
      isBootUnderlayReady() ||
      performance.now() - startedAt >= BOOT_SPLASH_UNDERLAY_TIMEOUT_MS
    ) {
      callback();
      return;
    }
    window.setTimeout(tick, BOOT_SPLASH_UNDERLAY_POLL_MS);
  };

  tick();
}

function scheduleAfterNextPaint(callback: () => void) {
  let firstFrame = 0;
  let secondFrame = 0;
  let fallback = 0;
  let done = false;

  const run = () => {
    if (done) {
      return;
    }

    done = true;
    callback();
  };

  firstFrame = window.requestAnimationFrame(() => {
    secondFrame = window.requestAnimationFrame(run);
  });
  fallback = window.setTimeout(run, BOOT_SPLASH_READY_FALLBACK_MS);

  return () => {
    done = true;
    window.cancelAnimationFrame(firstFrame);
    window.cancelAnimationFrame(secondFrame);
    window.clearTimeout(fallback);
  };
}

let bootSplashDismissing = false;

/** Several parts of the app report ready; the splash fades once. */
function dismissBootSplash() {
  const splash = document.getElementById('sl-boot-splash');
  if (!splash || bootSplashDismissing) {
    return;
  }
  bootSplashDismissing = true;

  waitForBootUnderlayReady(() => {
    recordBootEvent('boot-splash-fade-started');
    splash.setAttribute('data-state', 'fade');
    window.setTimeout(() => {
      recordBootEvent('boot-splash-removed');
      splash.remove();
      document.documentElement.removeAttribute('data-sl-boot-theme');
    }, BOOT_SPLASH_FADE_MS + 40);
  });
}

export function dispatchBootSplashReady() {
  completeBootTrace({ signal: 'app-shell-ready' });
  // The caller reports ready after the app shell has painted.
  dismissBootSplash();
}

export function useBootSplashReady(ready = true) {
  useEffect(() => {
    if (!ready) {
      return;
    }

    return scheduleAfterNextPaint(dispatchBootSplashReady);
  }, [ready]);
}
